import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';

import { Lock, LockOpen, InfoOutlined as IconInfo, EditOutlined as IconEdit } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';

import { Board } from '@/models/BoardModel';
import { Question } from '@/models/card/CardModel';
import { Authority } from '@/models/permission/AuthorityModel';

import { actions as boardActions, selectActiveBoard, selectRoomMembers } from '../../ducks/Boards';
import {
  selectCards,
  selectIsLoadingCards,
  selectLastActiveCard,
  selectListViewConfig,
  actions as cardActions,
} from '../../ducks/Cards';
import { exportContent } from '../../services/Export';
import { alertAction } from '../../thunks/Alerts';
import * as cardActionThunks from '../../thunks/Cards';

import {
  getCellComponentRenderer,
  itemToRow,
  toPrimitiveFieldValue,
  getCardInfo,
  getEntryTitle,
} from '../../utils/FieldUtil';
import { generateExportRequest } from '../../utils/ExportUtil';
import {
  questionFieldsToColumns,
  STEP_COLUMN,
  generateNumberOfCardsMessage,
  COMMENT_COLUMN,
  MARGIN_COLUMN,
  CONCLUDED_COLUMN,
} from '../../utils/ListViewUtil';
import { canEditQuestion } from '../../utils/PermissionUtils';
import { LIST_BOARD_VIEW_MODE } from '../../utils/ViewModeUtil';

import PageWrapper from '../../common/PageWrapper';
import { Cell } from '../../common/table';
import ToggleIconButton from '../../common/ToggleIconButton';
import ListBoard from './ListBoard';

import saveAs from 'file-saver';
import { ImmutableArray } from 'seamless-immutable';

let searchTerm = '';

const mapStateToProps = () =>
  createStructuredSelector({
    activeBoard: selectActiveBoard(),
    isLoadingCards: selectIsLoadingCards(),
    cards: selectCards(),
    lastActiveCard: selectLastActiveCard(),
    roomMembers: selectRoomMembers(),
    listViewConfig: selectListViewConfig(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({ ...cardActionThunks, ...cardActions, ...boardActions }, dispatch),
});

const ListBoardContainer = ({
  activeBoard,
  cards,
  lastActiveCard,
  isLoadingCards,
  roomMembers,
  listViewConfig,
  actions,
}: {
  activeBoard: Board;
  cards: ImmutableArray<Question>;
  lastActiveCard: Question;
  isLoadingCards: boolean;
  roomMembers: Authority[];
  listViewConfig: any;
  actions: any;
}) => {
  const navigate = useNavigate();
  const { communityId, roomId, boardId } = useParams();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState({ filters: [], viewConfig: listViewConfig });

  const step = searchParams.get('step');
  const isDecisionLog = !!(step && step === activeBoard.step_config.decision_log_step_id);

  useEffect(() => {
    actions.fetchCards({ boardId, communityId, roomId });

    searchTerm = localStorage.getItem('searchTerm') || '';
    setState((prev) => ({ ...prev, filters: JSON.parse(localStorage.getItem('filters') || '[]') || [] }));
  }, [communityId, roomId, boardId, actions]);

  useEffect(() => {
    return () => {
      const { viewConfig } = state;
      if (viewConfig) {
        actions.setListViewConfig(viewConfig);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.viewConfig, actions]);

  const handleSearch = (searchTerm: string) => {
    localStorage.setItem('searchTerm', searchTerm);
  };

  const handleFilter = (filters: any) => {
    localStorage.setItem('filters', JSON.stringify(filters));
    setState((prev) => ({ ...prev, filters: filters }));
  };

  const handleCardEditClick = (id: string) => {
    actions.setLastActiveCard({ lastActiveCard: id });

    return step
      ? navigate(`/${communityId}/${roomId}/boards/${boardId}/cards/${id}?view=${LIST_BOARD_VIEW_MODE}&step=${step}`)
      : navigate(`/${communityId}/${roomId}/boards/${boardId}/cards/${id}?view=${LIST_BOARD_VIEW_MODE}`);
  };

  const handleCardCreateClick = () =>
    navigate(`/${communityId}/${roomId}/boards/${boardId}/create-card?view=${LIST_BOARD_VIEW_MODE}`);

  const handleCardLockClick = (id: string) =>
    actions.lockCard({ communityId, roomId, boardId, cardId: id }).then((action: any) =>
      alertAction({
        action,
        error: intl.get('card.lock_unlock.error'),
        success: intl.get('card.lock_unlock.success'),
      }),
    );

  const handleCardUnlockClick = (id: string) =>
    actions.unlockCard({ communityId, roomId, boardId, cardId: id }).then((action: any) =>
      alertAction({
        action,
        error: intl.get('card.lock_unlock.error'),
        success: intl.get('card.lock_unlock.success'),
      }),
    );

  const handleExportClick = async ({
    columns,
    items,
    includeComments,
  }: {
    columns: any;
    items: any;
    includeComments: boolean;
  }) => {
    const columnsToExport = columns.filter(
      (column: { name: string }) =>
        column.name !== COMMENT_COLUMN &&
        column.name !== MARGIN_COLUMN &&
        (isDecisionLog || column.name !== CONCLUDED_COLUMN),
    );

    const folderName = activeBoard.workflow_config.workflow_name;
    const exportRequest = generateExportRequest({ columns: columnsToExport, items, includeComments });

    const response = await exportContent({ communityId, roomId, boardId, exportRequest });
    return await Promise.resolve(saveAs(response.data, `${folderName}.csv`));
  };

  const handleSortChange = (sorter: any) => {
    setState((prev) => ({ ...prev, viewConfig: { ...prev.viewConfig, sorter } }));
  };

  const handleColumnOrderChange = (columnOrder: any) => {
    setState((prev) => ({ ...prev, viewConfig: { ...prev.viewConfig, columnOrder } }));
  };

  const handleColumnVisibilityChange = (visibleColumns: any) => {
    setState((prev) => ({ ...prev, viewConfig: { ...prev.viewConfig, visibleColumns } }));
  };

  const rowToCardInfo = (row: any) => {
    const cardInfo = row.fields.reduce((questionInfo: any, field: any) => {
      questionInfo[field.id] = toPrimitiveFieldValue(field, roomMembers);
      return questionInfo;
    }, {});
    // Add step name and meta info to cardInfo
    return cardInfo.merge({ [STEP_COLUMN]: row[STEP_COLUMN], ...getCardInfo(row, roomMembers) });
  };

  const cardsToRows = (cards: ImmutableArray<Question>) => {
    const filteredCards = step ? cards.filter((card) => card.step.id === step) : cards;

    return filteredCards.asMutable().map((card: any) =>
      itemToRow({
        item: card,
        decision_log_step_id: activeBoard.step_config.decision_log_step_id,
        fieldConfig: activeBoard.field_config,
        roomMembers,
      }),
    );
  };

  const renderCardLinkButton = ({ row }: { row: any }) => {
    const canEdit = canEditQuestion(row.permissions);

    return (
      <Cell>
        <ToggleIconButton
          tooltipText={intl.get(
            canEdit ? 'board.views.list.tooltips.edit_question' : 'board.views.list.tooltips.view_question',
          )}
          generalIcon={canEdit ? <IconEdit /> : <IconInfo />}
          hoverIcon={canEdit ? <IconEdit /> : <IconInfo />}
          disabled={false}
          onClick={() => handleCardEditClick(row.id)}
        />
        <ToggleIconButton
          tooltipText={row.locked ? intl.get('card.unlock') : intl.get('card.lock')}
          generalIcon={row.locked ? <Lock /> : <LockOpen />}
          hoverIcon={row.locked ? <LockOpen /> : <Lock />}
          disabled={row.locked ? !row.permissions.UNLOCK : !row.permissions.LOCK}
          onClick={() => (row.locked ? handleCardUnlockClick(row.id) : handleCardLockClick(row.id))}
        />
      </Cell>
    );
  };

  if (isLoadingCards) {
    return <LinearProgress />;
  }

  return (
    <PageWrapper title={intl.get(isDecisionLog ? 'app_bar.decision_log' : 'app_bar.question_list_view')}>
      <ListBoard
        board={activeBoard}
        lastActiveItem={lastActiveCard}
        items={cardsToRows(cards)}
        height={window.innerHeight - 255}
        columns={questionFieldsToColumns(activeBoard.field_config)}
        renderCellComponent={getCellComponentRenderer(activeBoard.field_config.fields, roomMembers)}
        editing={{ renderLinkButtons: renderCardLinkButton, width: 100 }}
        rowToInfo={rowToCardInfo}
        readOnly={isDecisionLog ? !activeBoard.permissions.FULL_CONTROL : false}
        onItemCreateClick={handleCardCreateClick}
        tooltips={{ create: intl.get('board.tooltips.add_card') }}
        filters={state.filters}
        onFiltersChange={handleFilter}
        onSearch={handleSearch}
        onSortChange={handleSortChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        searchTerm={searchTerm}
        selection={{
          enableSelection: true,
          helperText: intl.get('board.views.list.tooltips.cards.export.helper_text'),
          numberOfItemsMessage: generateNumberOfCardsMessage,
        }}
        sorter={state.viewConfig.sorter}
        defaultSorter={[{ columnName: getEntryTitle(activeBoard.field_config.fields) }]}
        columnOrder={state.viewConfig.columnOrder}
        visibleColumns={state.viewConfig.visibleColumns}
        onExportClick={handleExportClick}
      />
    </PageWrapper>
  );
};

const ConnectedListBoardContainer = connect(mapStateToProps, mapDispatchToProps)(ListBoardContainer);

export default ConnectedListBoardContainer;
